import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import { tableParams } from './securitee'
import type { TablePage, TableRequest } from './softsee'

/**
 * Securitee > AD accounts (`SecuriteeAdAccountsController`): conturile din
 * Active Directory citite de agentul de pe controlorul de domeniu, o data pe
 * zi sau la "Read now". Momentele sunt UTC.
 */

export type AdCheckKey = 'never_expires' | 'old_password' | 'stale' | 'admins' | 'admins_delegable' | 'service_old'

export type AdPolicy = { min_length: number; max_age_days: number; complexity: boolean; history: number; lockout_threshold: number }

export type AdDomain = {
  domain: string
  device_id: number
  device_name: string | null
  account_count: number
  collected_at: string
  policy: AdPolicy | null
  policy_findings: string[]
  /** intarirea domeniului citita pe DC; null / lipsa la un agent sau un API mai vechi */
  hardening?: { dc: string; checks: AdHardeningCheck[] } | null
}

export type AdHardeningCheck = { key: string; ok: boolean; title: string; detail: string }

export type AdAccountsOverview = {
  domains: AdDomain[]
  checks: { key: AdCheckKey; label: string; count: number }[]
  total: number
  read: { dcs: number; pending: boolean }
  thresholds: { old_password_days: number; stale_days: number }
}

export type AdAccountRow = {
  DT_RowId: string
  sam: string
  name: string
  domain: string
  enabled: boolean
  pwd_set: string | null
  last_logon: string | null
  created: string | null
  admin_groups: string[]
  not_delegated: boolean
  protected_user: boolean
  spn: boolean
  flags: AdCheckKey[]
}

export function adAccountsQuery() {
  return queryOptions({
    queryKey: ['securitee', 'ad-accounts'],
    queryFn: () => apiGet<AdAccountsOverview>('/securitee/ad-accounts'),
  })
}

/**
 * `check`: o cheie din `checks` (cardul ales); '' = toate conturile. `stamp`
 * (cand s-au citit domeniile) e numai in cheie: o citire noua reface tabelul.
 */
export function adAccountsTableQuery(r: Omit<TableRequest, 'list'>, check: AdCheckKey | '', stamp = '') {
  const extra: Record<string, string> = check ? { check } : {}
  return queryOptions({
    queryKey: ['securitee', 'ad-accounts', 'table', r, extra, stamp],
    queryFn: () => apiGet<TablePage<AdAccountRow>>(`/securitee/ad-accounts/table?${tableParams(r, extra)}`),
  })
}

/** "Read now": comanda ADU pe controlorii de domeniu ai firmei. */
export function adAccountsRead() {
  return apiPost<{ queued: number }>('/securitee/ad-accounts/read', {})
}
